/**
 * Display formatters for `@richrich/ui`.
 *
 * SAFETY: money in richrich is a canonical decimal *string* (`Decimal`), never a
 * JS float (ARCHITECTURE §2). All *value* formatting here operates on the string
 * directly — grouping and rounding are done digit-by-digit so no precision is
 * lost through `Number()`. The only place a decimal is coerced to a JS number is
 * `decimalToNumber`, used strictly to derive **pixel coordinates** for a chart
 * canvas/SVG (a rendering approximation, never accounting) — see the chart
 * modules. This mirrors what `@richrich/core/money` does for arithmetic; the UI
 * package deliberately does not depend on core, so it re-implements only the
 * display slice.
 */
import type { Currency } from "@richrich/contracts";

/** Currency glyph for a given currency (branded-key-safe). */
function currencySymbol(currency: Currency): string {
  switch (currency) {
    case "KRW":
      return "₩";
    case "USD":
      return "$";
    default:
      return "";
  }
}

/** Canonical display precision per currency (KRW is integer-only). */
export function currencyFractionDigits(currency: Currency): number {
  return currency === "KRW" ? 0 : 2;
}

/** Coerce a decimal string to a plain number — ONLY for chart geometry. */
export function decimalToNumber(value: string): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function isZeroDigits(s: string): boolean {
  for (const ch of s) if (ch !== "0") return false;
  return true;
}

function stripLeadingZeros(s: string): string {
  let i = 0;
  while (i < s.length - 1 && s[i] === "0") i++;
  return s.slice(i);
}

/** Add 1 to a non-negative integer digit-string (handles carry / new digit). */
function incrementDigits(digits: string): string {
  const arr = digits.split("");
  let i = arr.length - 1;
  for (; i >= 0; i--) {
    const d = arr[i]!;
    if (d === "9") {
      arr[i] = "0";
    } else {
      arr[i] = String(Number(d) + 1);
      return arr.join("");
    }
  }
  return `1${arr.join("")}`;
}

/**
 * Round a canonical decimal string to `digits` fractional places, half-up
 * (away from zero). Returns a canonical decimal string; never touches floats.
 */
export function roundDecimal(value: string, digits: number): string {
  const negative = value.startsWith("-");
  const unsigned = negative ? value.slice(1) : value;
  const dot = unsigned.indexOf(".");
  const intPart = dot === -1 ? unsigned : unsigned.slice(0, dot);
  const fracPart = dot === -1 ? "" : unsigned.slice(dot + 1);

  let outInt: string;
  let outFrac: string;

  if (fracPart.length <= digits) {
    outInt = intPart;
    outFrac = fracPart.padEnd(digits, "0");
  } else {
    const keep = fracPart.slice(0, digits);
    const nextDigit = fracPart.charCodeAt(digits) - 48;
    let combined = intPart + keep;
    if (nextDigit >= 5) combined = incrementDigits(combined);
    const fracLen = digits;
    const intLen = combined.length - fracLen;
    outInt = intLen > 0 ? combined.slice(0, intLen) : "0";
    outFrac = combined.slice(Math.max(intLen, 0)).padStart(fracLen, "0");
  }

  outInt = stripLeadingZeros(outInt);
  const body = digits > 0 ? `${outInt}.${outFrac}` : outInt;
  const isZero = isZeroDigits(outInt) && isZeroDigits(outFrac);
  return negative && !isZero ? `-${body}` : body;
}

function groupThousands(intDigits: string): string {
  const negative = intDigits.startsWith("-");
  const digits = negative ? intDigits.slice(1) : intDigits;
  let out = "";
  for (let i = 0; i < digits.length; i++) {
    if (i > 0 && (digits.length - i) % 3 === 0) out += ",";
    out += digits[i];
  }
  return negative ? `-${out}` : out;
}

export interface FormatDecimalOptions {
  fractionDigits?: number;
  /** Prefix a `+` on positive, non-zero values (deltas). */
  signed?: boolean;
}

/**
 * Format a canonical decimal string with thousands grouping and fixed fraction
 * digits — string-exact, no float round-trip.
 */
export function formatDecimal(value: string, options: FormatDecimalOptions = {}): string {
  const digits = options.fractionDigits ?? 0;
  const rounded = roundDecimal(value, digits);
  const negative = rounded.startsWith("-");
  const unsigned = negative ? rounded.slice(1) : rounded;
  const dot = unsigned.indexOf(".");
  const intPart = dot === -1 ? unsigned : unsigned.slice(0, dot);
  const fracPart = dot === -1 ? "" : unsigned.slice(dot + 1);
  const grouped = groupThousands(intPart);
  const body = digits > 0 ? `${grouped}.${fracPart}` : grouped;
  if (negative) return `-${body}`;
  if (options.signed && !isZeroDigits(intPart)) return `+${body}`;
  if (options.signed && dot !== -1 && !isZeroDigits(fracPart)) return `+${body}`;
  return body;
}

export interface FormatMoneyOptions extends FormatDecimalOptions {
  /** Omit the currency symbol (e.g. inside a labelled column). */
  bare?: boolean;
}

/** Format a decimal string as money: `₩1,234,567` / `-$1,234.50`. */
export function formatMoney(
  value: string,
  currency: Currency,
  options: FormatMoneyOptions = {},
): string {
  const digits = options.fractionDigits ?? currencyFractionDigits(currency);
  const formatted = formatDecimal(value, {
    fractionDigits: digits,
    signed: options.signed ?? false,
  });
  if (options.bare) return formatted;
  const symbol = currencySymbol(currency);
  // Keep the sign in front of the symbol: `-₩1,000`, `+$5.00`.
  if (formatted.startsWith("-")) return `-${symbol}${formatted.slice(1)}`;
  if (formatted.startsWith("+")) return `+${symbol}${formatted.slice(1)}`;
  return `${symbol}${formatted}`;
}

/**
 * Compact money for dense axis ticks (`₩1.2M`, `$3.4B`). This is an *axis label*
 * approximation and therefore uses a number round-trip — never used to display
 * an exact balance. Input may be a decimal string or an already-derived number.
 */
export function formatCompactMoney(value: string | number, currency: Currency): string {
  const n = typeof value === "number" ? value : decimalToNumber(value);
  const symbol = currencySymbol(currency);
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  const units: Array<[number, string]> = [
    [1e12, "T"],
    [1e9, "B"],
    [1e6, "M"],
    [1e3, "K"],
  ];
  for (const [scale, suffix] of units) {
    if (abs >= scale) {
      const scaled = abs / scale;
      const digits = scaled >= 100 ? 0 : scaled >= 10 ? 1 : 2;
      return `${sign}${symbol}${scaled.toFixed(digits)}${suffix}`;
    }
  }
  const digits = currency === "KRW" ? 0 : 2;
  return `${sign}${symbol}${abs.toFixed(digits)}`;
}

/**
 * Format a ratio (0..1, e.g. TWR or an allocation weight) as a percentage. This
 * is an already-numeric ratio, so a number path is correct here.
 */
export function formatPercent(ratio: number, fractionDigits = 2, signed = false): string {
  if (!Number.isFinite(ratio)) return "—";
  const pct = ratio * 100;
  const sign = signed && pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(fractionDigits)}%`;
}

// Date formatting is locale-aware. Each formatter takes an optional trailing
// `locale` (a BCP-47 tag, e.g. `'ko'`); omitting it uses the JS runtime default
// (`undefined`), preserving every existing call site. Constructing an
// `Intl.DateTimeFormat` is comparatively expensive, so instances are cached per
// resolved locale (the empty-string key stands in for the runtime default).
const SHORT_DATE_OPTIONS: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
const LONG_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
};
const DATETIME_OPTIONS: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
};

/** A per-locale cache of `Intl.DateTimeFormat` for one fixed option set. */
function cachedFormatters(
  options: Intl.DateTimeFormatOptions,
): (locale: string | undefined) => Intl.DateTimeFormat {
  const cache = new Map<string, Intl.DateTimeFormat>();
  return (locale) => {
    const key = locale ?? "";
    let formatter = cache.get(key);
    if (formatter === undefined) {
      formatter = new Intl.DateTimeFormat(locale, options);
      cache.set(key, formatter);
    }
    return formatter;
  };
}

const shortDate = cachedFormatters(SHORT_DATE_OPTIONS);
const longDate = cachedFormatters(LONG_DATE_OPTIONS);
const dateTime = cachedFormatters(DATETIME_OPTIONS);

/** `Aug 12` / `8. 12.` — compact axis tick from epoch-ms. */
export function formatDateShort(epochMs: number, locale?: string): string {
  return shortDate(locale).format(epochMs);
}

/** `Aug 12, 2026` / `2026. 8. 12.` — tooltip / long label from epoch-ms. */
export function formatDateLong(epochMs: number, locale?: string): string {
  return longDate(locale).format(epochMs);
}

/** `Aug 12, 14:30` / `8. 12. 14:30` — intraday label from epoch-ms. */
export function formatDateTime(epochMs: number, locale?: string): string {
  return dateTime(locale).format(epochMs);
}

/** Sign of a decimal string (-1 | 0 | 1) without float coercion for the zero test. */
export function decimalSign(value: string): number {
  if (value.startsWith("-")) {
    return isZeroDigits(value.replace(/[-.]/g, "")) ? 0 : -1;
  }
  return isZeroDigits(value.replace(/[.]/g, "")) ? 0 : 1;
}
